// src/pages/nfl/NflShowdownPosProjections.jsx
// Updated: opponent logos and kickoff-only Time ET (no date).
import React, { useEffect, useMemo, useState } from "react";
/* ---------------- data sources (SHOWDOWN) ---------------- */
const POS_LIST = ["QB", "RB", "WR", "TE", "K", "DST"];
const PLAYER_SIMULATIONS_URL = "/data/nfl/classic/latest/playersimulations.json";
const SITE_IDS_URL = "/data/nfl/showdown/latest/site_ids.json";
const VEGAS_URL = "/data/nfl/classic/latest/vegas.json";
const SIM_META_URL = "/data/nfl/classic/latest/meta.json";
const PROJECTIONS_URL = "/data/nfl/showdown/latest/projections.json";
const teamLogo = (abbr) => (abbr ? `/logos/nfl/${abbr}.png` : "");
/* ---------------- helpers ---------------- */
const num = (v) => {
  if (v == null || String(v).trim() === "") return null;
  const n = Number(String(v).replace(/[$,%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const pct = (v) => {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  if (s.endsWith("%")) return s;
  const n = num(s);
  if (n == null) return "";
  const val =
    Math.abs(n) <= 1 ? (n * 100).toFixed(1) : n.toFixed(1);
  return `${val}%`;
};
const smart1 = (v) => {
  const n = num(v);
  return n == null ? "" : Number.isInteger(n) ? String(n) : n.toFixed(1);
};
const int0 = (v) => {
  const n = num(v);
  return n == null ? "" : Math.round(n).toLocaleString();
};
const normalizeName = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.'’,-]/g, " ")
    .replace(/\s+(jr|sr|ii|iii|iv)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
/* canonicalize header keys */
const canonKey = (s = "") =>
  String(s)
    .replace(/\u00A0|\u202F/g, " ")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[%]/g, "")
    .replace(/_/g, " ")
    .replace(/[.]/g, "")
    .replace(/\s+/g, "");
const buildKeyMap = (row) => {
  const m = {};
  for (const [k, v] of Object.entries(row || {})) m[canonKey(k)] = v;
  return m;
};
const getVal = (kmap, ...cands) => {
  for (const c of cands) {
    const v = kmap[canonKey(c)];
    if (v !== undefined) return v;
  }
  return "";
};
async function fetchJson(url, whole = false) {
  const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const j = await r.json();
  return whole ? j : Array.isArray(j) ? j : j?.rows ?? j?.players ?? j?.data ?? [];
}
function useJson(url, whole = false) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let ok = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const data = await fetchJson(url, whole);
        if (ok) setRows(data);
      } catch (e) {
        if (ok) { setRows(whole ? {} : []); setErr(String(e)); }
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [url, whole]);
  return { rows, loading, err };
}
/* meta fetch (for last-updated tag) */
function useMeta(url) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error();
        const j = await r.json();
        if (ok) setMeta(j);
      } catch {
        if (ok) setMeta(null);
      }
    })();
    return () => {
      ok = false;
    };
  }, [url]);
  return meta;
}
const numberLike = value => num(value) ?? 0;
const SITES = { dk: { capTag: "CPT", flexFieldPrefix: "DK FLEX", mvpFieldPrefix: "DK CPT" }, fd: { capTag: "MVP", flexFieldPrefix: "FD FLEX", mvpFieldPrefix: "FD MVP" } };
const NFL_TEAMS = {
  ARI:{city:"Arizona",nickname:"Cardinals"}, ATL:{city:"Atlanta",nickname:"Falcons"},
  BAL:{city:"Baltimore",nickname:"Ravens"}, BUF:{city:"Buffalo",nickname:"Bills"},
  CAR:{city:"Carolina",nickname:"Panthers"}, CHI:{city:"Chicago",nickname:"Bears"},
  CIN:{city:"Cincinnati",nickname:"Bengals"}, CLE:{city:"Cleveland",nickname:"Browns"},
  DAL:{city:"Dallas",nickname:"Cowboys"}, DEN:{city:"Denver",nickname:"Broncos"},
  DET:{city:"Detroit",nickname:"Lions"}, GB:{city:"Green Bay",nickname:"Packers"},
  HOU:{city:"Houston",nickname:"Texans"}, IND:{city:"Indianapolis",nickname:"Colts"},
  JAX:{city:"Jacksonville",nickname:"Jaguars"}, KC:{city:"Kansas City",nickname:"Chiefs"},
  LAC:{city:"Los Angeles",nickname:"Chargers"}, LAR:{city:"Los Angeles",nickname:"Rams"},
  LV:{city:"Las Vegas",nickname:"Raiders"}, MIA:{city:"Miami",nickname:"Dolphins"},
  MIN:{city:"Minnesota",nickname:"Vikings"}, NE:{city:"New England",nickname:"Patriots"},
  NO:{city:"New Orleans",nickname:"Saints"}, NYG:{city:"New York",nickname:"Giants"},
  NYJ:{city:"New York",nickname:"Jets"}, PHI:{city:"Philadelphia",nickname:"Eagles"},
  PIT:{city:"Pittsburgh",nickname:"Steelers"}, SEA:{city:"Seattle",nickname:"Seahawks"},
  SF:{city:"San Francisco",nickname:"49ers"}, TB:{city:"Tampa Bay",nickname:"Buccaneers"},
  TEN:{city:"Tennessee",nickname:"Titans"}, WAS:{city:"Washington",nickname:"Commanders"},
};
function sdPick(obj, variants, fallback = 0) {
  for (const v of variants) {
    if (obj == null) break;
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase().replace(/[^a-z0-9]/g,"") === v.toLowerCase().replace(/[^a-z0-9]/g,"")) {
        return obj[k];
      }
    }
  }
  return fallback;
}
/* --------------------- name/pos/team normalization ----------------- */
function sdNormName(s){return String(s||"").toLowerCase().replace(/\u2019/g,"'").replace(/\./g,"").replace(/,\s*(jr|sr)\b/g,"").replace(/\b(jr|sr)\b/g,"").replace(/[^a-z' -]/g,"").replace(/\s+/g," ").trim();}
function normTeam(s){ const t = String(s || "").toUpperCase().trim(); return t === "JAC" ? "JAX" : t === "WSH" ? "WAS" : t; }
function normPosRaw(s){return (s||"").toUpperCase().trim();}
function sdNormPos(s){ const p = normPosRaw(s); return ["D", "DEF", "DEFENSE", "D/ST", "DST"].includes(p) ? "DST" : p; }
function inferTeamFromNameForDST(name) {
  const nm = sdNormName(name);
  // Nicknames identify LA/NY defenses without ambiguous shared-city matches.
  for (const [abbr, team] of Object.entries(NFL_TEAMS)) {
    if (nm === sdNormName(team.nickname) || nm === sdNormName(`${team.city} ${team.nickname}`) || nm.includes(sdNormName(team.nickname))) return abbr;
  }
  const cities = Object.entries(NFL_TEAMS).filter(([, team]) => nm === sdNormName(team.city));
  return cities.length === 1 ? cities[0][0] : "";
}
// Shared data stays separate from site/slate-specific salary and upload IDs.
const rowsOf = (data) => Array.isArray(data) ? data : data?.rows ?? data?.players ?? data?.data ?? [];
function showdownPlayerKey(name, team, pos = "") {
  const tm = normTeam(team);
  const dst = sdNormPos(pos) === "DST" || (tm && inferTeamFromNameForDST(name) === tm);
  return `${tm}|${dst ? "DST" : sdNormName(name)}`;
}
function availableShowdownSlates(data, site) {
  // Old JSON remains usable while the replacement exporter is being installed.
  const source = Array.isArray(data?.slates) ? data.slates
    : data && (data.dk?.length || data.fd?.length) ? [{ ...data, key: "legacy", label: "Current showdown" }] : [];
  return source.filter((s) => Array.isArray(s[site]) && s[site].length > 0)
    .slice().sort((a, b) => String(a.gameDate || "").localeCompare(String(b.gameDate || "")) || a.key.localeCompare(b.key));
}
// Interpret salary-file wall-clock timestamps in Eastern time, regardless of
// the user's browser timezone, including Eastern standard/daylight time.
function showdownKickoffMillis(slate) {
  const raw = String(slate?.gameDate || "").trim();
  if (!raw) return NaN;
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return Date.parse(raw);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return NaN;
  const target = Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +(match[6] || 0));
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: slate.timezone || "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  });
  let instant = target;
  for (let attempt = 0; attempt < 3; attempt++) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(instant)).map(({ type, value }) => [type, value]));
    const displayed = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    const correction = target - displayed;
    if (!correction) return instant;
    instant += correction;
  }
  return instant;
}
function chooseShowdownSlate(slates, selection, now) {
  const choices = slates.map((slate) => ({ slate, kickoff: showdownKickoffMillis(slate) }))
    .sort((a, b) => (Number.isFinite(a.kickoff) ? a.kickoff : Infinity) - (Number.isFinite(b.kickoff) ? b.kickoff : Infinity));
  const manual = selection && choices.find(({ slate }) => slate.key === selection.key);
  // A future game selected manually advances once it locks. Selecting a game
  // that was already locked deliberately opens its history for review.
  if (manual && (!Number.isFinite(manual.kickoff) || now < manual.kickoff || selection.selectedAt >= manual.kickoff)) return manual.slate;
  const upcoming = choices.find(({ kickoff }) => Number.isFinite(kickoff) && kickoff > now);
  if (upcoming) return upcoming.slate;
  // If every game has locked, keep the latest game available for review.
  return choices.filter(({ kickoff }) => Number.isFinite(kickoff)).at(-1)?.slate || choices[0]?.slate || null;
}
function buildShowdownRows({ slate, site, simulations, projections, legacyIds, customProj }) {
  if (!slate) return [];
  const scheduleTeams = [slate.away, slate.home].filter(Boolean).map(normTeam);
  const records = slate[site] || [];
  const groups = new Map();
  for (const rec of records) {
    const tm = normTeam(rec.team);
    const k = showdownPlayerKey(rec.name, tm, rec.player_position || rec.pos);
    const g = groups.get(k) || { name: rec.name, team: tm, records: [] };
    g.records.push(rec); groups.set(k, g);
  }
  const index = (data) => new Map(rowsOf(data).map((r) => [
    showdownPlayerKey(r.player ?? r.name ?? r.Player, r.team ?? r.Team, r.pos ?? r.Pos), r,
  ]));
  const simMap = index(simulations), projMap = index(projections);
  const originalKeys = new Set((legacyIds?.[site] || []).map((r) => r.slate_key).filter(Boolean));
  // The old projections workbook only supplies rates for its original game.
  const originalGame = slate.key === "legacy" || originalKeys.has(slate.key);
  const pct = (v) => {
    if (v == null || String(v).trim() === "") return null;
    const n = numberLike(v);
    return String(v).includes("%") || n > 1 ? n / 100 : n;
  };
  const output = [];
  for (const [k, group] of groups) {
    const sim = simMap.get(k), old = originalGame ? projMap.get(k) : null;
    const flex = site === "dk" ? group.records.find((r) => r.pos === "FLEX") : group.records[0];
    const cap = site === "dk" ? group.records.find((r) => r.pos === "CPT") : flex;
    if (!flex || !cap || !flex.id || !cap.id) continue;
    const salary = numberLike(site === "dk" ? flex.salary : flex.salary_flex);
    const capSalary = numberLike(site === "dk" ? cap.salary : cap.salary_mvp);
    if (salary <= 0 || capSalary <= 0) continue;
    const source = sim || old || {};
    const name = sim?.player ?? old?.player ?? old?.Player ?? group.name;
    const pos = sdNormPos(source.pos ?? source.Pos ?? flex.player_position ?? flex.pos);
    const realPos = ["QB", "RB", "WR", "TE", "DST", "K"].includes(pos) ? pos : (k.endsWith("|DST") ? "DST" : "");
    const baseProj = numberLike(sdPick(source, [`${site}_proj`, `${site} proj`, `${SITES[site].flexFieldPrefix} proj`], 0));
    const floor = numberLike(sdPick(source, [`${site}_floor`, `${SITES[site].flexFieldPrefix} floor`], 0));
    const ceil = numberLike(sdPick(source, [`${site}_ceiling`, `${site}_ceil`, `${SITES[site].flexFieldPrefix} ceiling`], 0));
    const capTag = SITES[site].capTag;
    const slotRate = (field, isCap) => {
      if (!old) return null;
      const prefix = isCap ? SITES[site].mvpFieldPrefix : SITES[site].flexFieldPrefix;
      const tag = capTag.toLowerCase();
      const fields = isCap
        ? [`${site}_${tag}_${field}`, `${site}_${tag}_${field}%`, `${prefix} ${field}%`, `${prefix} ${field}`]
        : [`${site}_${field}`, `${site}_${field}%`, `${prefix} ${field}%`, `${prefix} ${field}`];
      return pct(sdPick(old, fields, null));
    };
    const customFlex = customProj?.[`${name}::FLEX`];
    const customCap = customProj?.[`${name}::${capTag}`];
    const hasFlexCustom = customFlex != null && String(customFlex).trim() !== "";
    const hasCapCustom = customCap != null && String(customCap).trim() !== "";
    // Captain/MVP receives 1.5x points. Salaries always come from the salary file.
    const baseCapProj = baseProj * 1.5;
    output.push({
      name, team: group.team, pos: realPos,
      opp: scheduleTeams.find((tm) => tm !== group.team) || source.opp || source.Opp || "",
      salary, cap_salary: capSalary,
      proj: hasFlexCustom ? Math.max(0, numberLike(customFlex)) : baseProj,
      cap_proj: hasCapCustom ? Math.max(0, numberLike(customCap)) : baseCapProj,
      base_proj: baseProj, base_cap_proj: baseCapProj,
      custom_proj: hasFlexCustom ? customFlex : "", custom_cap_proj: hasCapCustom ? customCap : "",
      has_custom_proj: hasFlexCustom, has_custom_cap_proj: hasCapCustom,
      floor, ceil, cap_floor: floor * 1.5, cap_ceil: ceil * 1.5,
      pown: slotRate("pown", false), opt: slotRate("opt", false),
      cap_pown: slotRate("pown", true), cap_opt: slotRate("opt", true),
      hasProjection: !!(sim || old) && baseProj > 0,
      siteIdOnly: !(sim || old) || baseProj <= 0,
      siteId: String(flex.id), capSiteId: String(cap.id),
      siteName: flex.name, capSiteName: cap.name,
      fdPrefix: slate.fd_prefix || flex.prefix || null,
      slateKey: slate.key, __raw: source,
    });
  }
  return output;
}
// Salary records control game membership; simulations supply ordinary stats.
// Switching roster role changes fantasy points/salary, never the stat forecasts.
function buildShowdownProjectionFeed({ slate, siteIds, simulations, projections, vegasRows, site, rosterRole }) {
  if (!slate) return [];
  const maps = {};
  for (const key of ["dk", "fd"]) maps[key] = new Map(buildShowdownRows({ slate, site: key, simulations, projections, legacyIds: siteIds, customProj: {} })
    .map(r => [showdownPlayerKey(r.name, r.team, r.pos), r]));
  const keys = site === "both" ? new Set([...maps.dk.keys(), ...maps.fd.keys()]) : new Set(maps[site].keys());
  const schedule = new Map(rowsOf(vegasRows).map(r => [normTeam(r.team ?? r.Team), r]));
  const kickoff = showdownKickoffMillis(slate);
  const time = Number.isFinite(kickoff) ? new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York",
    hour: "numeric", minute: "2-digit" }).format(new Date(kickoff)) : "";
  return [...keys].map(key => {
    const r = maps[site === "fd" ? "fd" : "dk"].get(key) || maps.fd.get(key);
    const game = schedule.get(r.team);
    const row = { ...r.__raw, player: r.name, team: r.team, pos: r.pos,
      opp: r.opp || normTeam(game?.opponent ?? game?.opp), time };
    for (const key of ["dk", "fd"]) {
      const p = maps[key].get(showdownPlayerKey(r.name, r.team, r.pos));
      const cap = rosterRole === "multiplier";
      const hasForecast = p && Object.keys(p.__raw || {}).length > 0;
      row[`${key}_sal`] = p ? cap ? p.cap_salary : p.salary : null;
      row[`${key}_id`] = p ? cap ? p.capSiteId : p.siteId : null;
      row[`${key}_proj`] = hasForecast ? cap ? p.cap_proj : p.proj : null;
      row[`${key}_floor`] = hasForecast ? cap ? p.cap_floor : p.floor : null;
      row[`${key}_ceiling`] = hasForecast ? cap ? p.cap_ceil : p.ceil : null;
      row[`${key}_pown`] = p ? cap ? p.cap_pown : p.pown : null;
      row[`${key}_opt`] = p ? cap ? p.cap_opt : p.opt : null;
      const salary = row[`${key}_sal`], projection = row[`${key}_proj`];
      row[`${key}_val`] = salary > 0 && projection != null ? projection / (salary / 1000) : null;
    }
    return row;
  }).filter(r => POS_LIST.includes(r.pos));
}
/* ---------------- column sets ---------------- */
const COLS_COMMON = [
  { id: "player", label: "Player", type: "text", w: "min-w-[12rem] text-left" },
  { id: "team", label: "Team", type: "team", w: "min-w-[4.5rem]" },
  { id: "opp", label: "Opp", type: "team" },
  { id: "time", label: "Time ET", type: "text", w: "whitespace-nowrap" },
  { id: "dk_sal", label: "DK Sal", type: "money", w: "min-w-[4.25rem]" },
  { id: "fd_sal", label: "FD Sal", type: "money", w: "min-w-[4.25rem]" },
];
const COLS_QB = [
  ...COLS_COMMON,
  { id: "pa_yards", label: "Pa Yards", type: "num1" },
  { id: "pa_att", label: "Pa Att", type: "num1" },
  { id: "pa_comp", label: "Pa Comp", type: "num1" },
  { id: "pa_pct", label: "Comp%", type: "pct" },
  { id: "pa_td", label: "Pa TD", type: "num1" },
  { id: "int", label: "INT", type: "num1" },
  { id: "ru_att", label: "Rush Attempts", type: "num1" },
  { id: "ypc", label: "YPC", type: "num1" },
  { id: "ru_yds", label: "Rush Yards", type: "num1" },
  { id: "ru_td", label: "Rush TD", type: "num1" },
  { id: "dk_proj", label: "DK Proj", type: "num1" },
  { id: "dk_val", label: "DK Val", type: "num1" },
  { id: "fd_proj", label: "FD Proj", type: "num1" },
  { id: "fd_val", label: "FD Val", type: "num1" },
];
const COLS_RB = [
  ...COLS_COMMON,
  { id: "ru_att", label: "Rush Attempts", type: "num1" },
  { id: "ypc", label: "YPC", type: "num1" },
  { id: "ru_yds", label: "Rush Yards", type: "num1" },
  { id: "ru_td", label: "Rush TD", type: "num1" },
  { id: "targets", label: "Targets", type: "num1" },
  { id: "tgt_share", label: "Tgt Share", type: "pct" },
  { id: "rec", label: "Rec", type: "num1" },
  { id: "rec_yds", label: "Rec Yards", type: "num1" },
  { id: "rec_td", label: "Rec TD", type: "num1" },
  { id: "dk_proj", label: "DK Proj", type: "num1" },
  { id: "dk_val", label: "DK Val", type: "num1" },
  { id: "fd_proj", label: "FD Proj", type: "num1" },
  { id: "fd_val", label: "FD Val", type: "num1" },
];
const COLS_TE = [
  ...COLS_COMMON,
  { id: "targets", label: "Targets", type: "num1" },
  { id: "tgt_share", label: "Tgt Share", type: "pct" },
  { id: "rec", label: "Rec", type: "num1" },
  { id: "rec_yds", label: "Rec Yards", type: "num1" },
  { id: "rec_td", label: "Rec TD", type: "num1" },
  { id: "dk_proj", label: "DK Proj", type: "num1" },
  { id: "dk_val", label: "DK Val", type: "num1" },
  { id: "fd_proj", label: "FD Proj", type: "num1" },
  { id: "fd_val", label: "FD Val", type: "num1" },
];
const COLS_WR = [
  ...COLS_COMMON,
  { id: "ru_att", label: "Rush Attempts", type: "num1" },
  { id: "ypc", label: "YPC", type: "num1" },
  { id: "ru_yds", label: "Rush Yards", type: "num1" },
  { id: "ru_td", label: "Rush TD", type: "num1" },
  { id: "targets", label: "Targets", type: "num1" },
  { id: "tgt_share", label: "Tgt Share", type: "pct" },
  { id: "rec", label: "Rec", type: "num1" },
  { id: "rec_yds", label: "Rec Yards", type: "num1" },
  { id: "rec_td", label: "Rec TD", type: "num1" },
  { id: "dk_proj", label: "DK Proj", type: "num1" },
  { id: "dk_val", label: "DK Val", type: "num1" },
  { id: "fd_proj", label: "FD Proj", type: "num1" },
  { id: "fd_val", label: "FD Val", type: "num1" },
];
const COLS_K = [
  ...COLS_COMMON,
  { id: "xpa", label: "XPA", type: "num1" },
  { id: "xp_m", label: "XP M", type: "num1" },
  { id: "fga", label: "FGA", type: "num1" },
  { id: "fg_m", label: "FG M", type: "num1" },
  { id: "fg_0_39", label: "0-39", type: "num1" },
  { id: "fg_30_39", label: "30-39", type: "num1" },
  { id: "fg_40_49", label: "40-49", type: "num1" },
  { id: "fg_50p", label: "50+", type: "num1" },
  { id: "dk_proj", label: "DK Proj", type: "num1" },
  { id: "dk_val", label: "DK Val", type: "num1" },
  { id: "fd_proj", label: "FD Proj", type: "num1" },
  { id: "fd_val", label: "FD Val", type: "num1" },
];
const COLS_DST = [
  { id: "team", label: "Team", type: "team" },
  { id: "opp", label: "Opp", type: "team" },
  { id: "time", label: "Time ET", type: "text" },
  { id: "player", label: "DST", type: "text", w: "min-w-[12rem] text-left" },
  { id: "dk_sal", label: "DK Sal", type: "money", w: "min-w-[4.25rem]" },
  { id: "fd_sal", label: "FD Sal", type: "money", w: "min-w-[4.25rem]" },
  { id: "sacks", label: "Sacks", type: "num1" },
  { id: "int", label: "Int", type: "num1" },
  { id: "fumbles", label: "Fumbles", type: "num1" },
  { id: "def_td", label: "DefTD", type: "num1" },
  { id: "points", label: "Points", type: "num1" },
  { id: "dk_proj", label: "DK Proj", type: "num1" },
  { id: "dk_val", label: "DK Val", type: "num1" },
  { id: "fd_proj", label: "FD Proj", type: "num1" },
  { id: "fd_val", label: "FD Val", type: "num1" },
];
const POINT_COLS = [
  { id: "dk_floor", label: "DK Floor", type: "num1" },
  { id: "dk_ceiling", label: "DK Ceiling", type: "num1" },
  { id: "dk_pown", label: "DK Own%", type: "pct" },
  { id: "dk_opt", label: "DK Opt%", type: "pct" },
  { id: "fd_floor", label: "FD Floor", type: "num1" },
  { id: "fd_ceiling", label: "FD Ceiling", type: "num1" },
  { id: "fd_pown", label: "FD Own%", type: "pct" },
  { id: "fd_opt", label: "FD Opt%", type: "pct" },
];
const POS_TO_COLS = {
  QB: COLS_QB,
  RB: COLS_RB,
  WR: COLS_WR,
  TE: COLS_TE,
  K: COLS_K,
  DST: COLS_DST,
};
/* ---------------- CSV ---------------- */
const escapeCSV = (s) =>
  /[",\r\n]/.test(String(s ?? ""))
    ? `"${String(s ?? "").replace(/"/g, '""')}"`
    : String(s ?? "");
const formatVal = (type, raw) =>
  type === "money"
    ? int0(raw)
    : type === "pct"
    ? pct(raw)
    : type === "num1"
    ? smart1(raw)
    : raw ?? "";
function downloadCSV(rows, cols, fname) {
  const header = cols.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      cols.map((c) => escapeCSV(formatVal(c.type, r[c.id]))).join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}
/* ---------------- heatmap palette ---------------- */
function dirFor(colId, activePos) {
  if (!colId) return null;
  const id = String(colId).toLowerCase();
  if (id.includes("sal") || id === "opp" || id === "time") return null;
  if (id.endsWith("_pown")) return "lower";
  if (id === "team" || id === "player") return null;
  // QB: INT lower is better; DST: INT higher is better
  if (id === "int") return activePos === "QB" ? "lower" : "higher";
  // DK/FD projections/value: higher better
  if (/(_proj|_val)$/.test(id)) return "higher";
  return "higher";
}
function heatTextColor(min, max, v, dir, theme = "dark") {
  if (v == null || min == null || max == null || min === max || !dir) return null;
  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  const isDark = theme === "dark";
  let hue;
  let sat;
  let light;
  if (isDark) {
    if (t < 0.5) {
      const u = t / 0.5; // red -> yellow
      hue = 0 + u * 60;
      sat = 80;
      light = 62 + u * 10;
    } else {
      const u = (t - 0.5) / 0.5; // yellow -> green
      hue = 60 + u * 60;
      sat = 75;
      light = 72 - u * 8;
    }
  } else {
    if (t < 0.5) {
      const u = t / 0.5;
      hue = 0 + u * 60;
      sat = 85;
      light = 42 + u * 6;
    } else {
      const u = (t - 0.5) / 0.5;
      hue = 60 + u * 60;
      sat = 80;
      light = 38 - u * 4;
    }
  }
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}
/* ---------------- component ---------------- */
export default function NflShowdownPosProjections({ pos }) {
  // default pos: from prop → URL ?pos= → QB
  const initialPos = useMemo(() => {
    const qp = new URLSearchParams(window.location.search);
    const fromQuery = qp.get("pos");
    const p = String(pos || fromQuery || "QB").toUpperCase();
    return POS_LIST.includes(p) ? p : "QB";
  }, [pos]);
  const [activePos, setActivePos] = useState(initialPos);
  // keep URL in sync (so refresh keeps the same position)
  useEffect(() => {
    const qp = new URLSearchParams(window.location.search);
    qp.set("pos", activePos);
    const next = `${window.location.pathname}?${qp.toString()}`;
    window.history.replaceState({}, "", next);
  }, [activePos]);
  const src = PLAYER_SIMULATIONS_URL;
  const baseCols = useMemo(() => [...(POS_TO_COLS[activePos] || POS_TO_COLS.QB), ...POINT_COLS], [activePos]);
  const { rows: simulations, loading: simsLoading, err: simsErr } = useJson(src);
  const { rows: projections } = useJson(PROJECTIONS_URL);
  const { rows: siteIds, loading: idsLoading, err: idsErr } = useJson(SITE_IDS_URL, true);
  const { rows: vegasRows } = useJson(VEGAS_URL);
  const meta = useMeta(SIM_META_URL);
  const [slateSite, setSlateSite] = useState("both");
  const [rosterRole, setRosterRole] = useState("flex");
  const [slateSelection, setSlateSelection] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const availableSlates = useMemo(() => slateSite === "both"
    ? [...new Map([...availableShowdownSlates(siteIds, "dk"), ...availableShowdownSlates(siteIds, "fd")].map(s => [s.key, s])).values()]
    : availableShowdownSlates(siteIds, slateSite), [siteIds, slateSite]);
  const activeSlate = chooseShowdownSlate(availableSlates, slateSelection?.site === slateSite ? slateSelection : null, now);
  const slate = activeSlate?.key || "none";
  const rawRows = useMemo(() => buildShowdownProjectionFeed({ slate: activeSlate, siteIds, simulations, projections,
    vegasRows, site: slateSite, rosterRole }).filter(r => r.pos === activePos),
    [activeSlate, siteIds, simulations, projections, vegasRows, slateSite, rosterRole, activePos]);
  const loading = simsLoading || idsLoading;
  const err = simsErr || idsErr;
  // theme
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
  // normalize each row to a stable shape
  const rows = useMemo(
    () =>
      rawRows.map((row) => {
        const k = buildKeyMap(row);
        const player = (
          getVal(
            k,
            "player",
            "player name",
            "name",
            "k", // kicker
            "dst",
            "defense",
            "qb",
            "rb",
            "rb1",
            "wr",
            "wr1",
            "te",
            "te1"
          ) || ""
        )
          .toString()
          .trim();
        let team = (getVal(k, "team", "teamabbrev") || "")
          .toString()
          .trim()
          .toUpperCase();
        const dk_sal = getVal(k, "dk sal", "dk_sal", "dk\u00a0sal");
        const fd_sal = getVal(k, "fd sal", "fd_sal", "fd\u00a0sal");
        // QB
        const pa_yards = getVal(k, "pa yards", "pass yards", "pa_yards");
        const pa_att = getVal(k, "pa att", "pass attempts", "pa attempts", "pa_att");
        const pa_comp = getVal(k, "pa comp", "pass comp", "pa_comp");
        const pa_pct = getVal(
          k,
          "pa_comp_pct",
          "comp%",
          "completion%",
          "pass comp%",
          "pa comp%"
        );
        const pa_td = getVal(k, "pa td", "pa_td");
        const intVal = getVal(k, "int", "INT");
        // rushing
        const ru_att = getVal(
          k,
          "rush attempts",
          "rush att",
          "rushing attempts",
          "ru attempts",
          "ru att",
          "ru_att",
          "ru attempts",
          "rushattempts"
        );
        const ypc = getVal(k, "ypc");
        const ru_yds = getVal(
          k,
          "rush yards",
          "rushing yards",
          "ru yards",
          "ru_yds",
          "ru_yards"
        );
        const ru_td = getVal(k, "rush td", "rushing td", "ru td", "ru_td");
        // receiving
        const targets = getVal(k, "targets");
        const tgt_share = getVal(k, "tgt share", "target share", "tgt_share");
        const rec = getVal(k, "rec", "receptions");
        const rec_yds = getVal(
          k,
          "rec yards",
          "receiving yards",
          "rec yds",
          "rec_yds",
          "rec_yards"
        );
        const rec_td = getVal(k, "rec td", "rec_td", "receiving td");
        // K
        const xpa = getVal(k, "xpa", "xp_att");
        const xp_m = getVal(k, "xp m", "xpm", "xp_m", "xp_made");
        const fga = getVal(k, "fga", "fg_att");
        const fg_m = getVal(k, "fg m", "fgm", "fg_m", "fg_made");
        const fg_0_39 = getVal(k, "fg0_39_made", "0-39", "fg 0-39");
        const fg_30_39 = getVal(k, "30-39", "30 39", "3039", "fg 30-39");
        const fg_40_49 = getVal(k, "40-49", "40 49", "4049", "fg 40-49", "fg40_49_made");
        const fg_50p = getVal(k, "50+", "50", "50plus", "fg 50+", "50+ fg", "fg50p_made");
        // DST
        const sacks = getVal(k, "sacks");
        const fumbles = getVal(k, "fumbles");
        const def_td = getVal(k, "deftd", "def td", "def_td");
        const points = getVal(k, "points");
        // DK/FD
        const dk_proj = getVal(k, "dk proj", "dk_proj", "dk\u00a0proj");
        const dk_val = getVal(k, "dk val", "dk_val", "dk\u00a0val");
        const fd_proj = getVal(k, "fd proj", "fd_proj", "fd\u00a0proj");
        const fd_val = getVal(k, "fd val", "fd_val", "fd\u00a0val");
        return {
          player,
          team,
          opp: row.opp,
          time: row.time,
          dk_floor: row.dk_floor, dk_ceiling: row.dk_ceiling, dk_pown: row.dk_pown, dk_opt: row.dk_opt,
          fd_floor: row.fd_floor, fd_ceiling: row.fd_ceiling, fd_pown: row.fd_pown, fd_opt: row.fd_opt,
          dk_sal,
          fd_sal,
          // QB
          pa_yards,
          pa_att,
          pa_comp,
          pa_pct,
          pa_td,
          int: intVal,
          // RB/WR/TE rushing+receiving
          ru_att,
          ypc,
          ru_yds,
          ru_td,
          targets,
          tgt_share,
          rec,
          rec_yds,
          rec_td,
          // K
          xpa,
          xp_m,
          fga,
          fg_m,
          fg_0_39,
          fg_30_39,
          fg_40_49,
          fg_50p,
          // DST
          sacks,
          fumbles,
          def_td,
          points,
          // DK/FD
          dk_proj,
          dk_val,
          fd_proj,
          fd_val,
        };
      }),
    [rawRows]
  );
  // search / team filter / WR rushing visibility
  const [q, setQ] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("ALL");
  const [hideWrRushing, setHideWrRushing] = useState(true);
  const teams = useMemo(
    () =>
      [...new Set(rows.map((r) => String(r.team || "").trim().toUpperCase()).filter(Boolean))].sort(),
    [rows]
  );
  useEffect(() => {
    if (selectedTeam !== "ALL" && !teams.includes(selectedTeam)) {
      setSelectedTeam("ALL");
    }
  }, [teams, selectedTeam]);
  const cols = useMemo(() => {
    let result = baseCols;
    if (activePos === "WR" && hideWrRushing) {
      const rushing = new Set(["ru_att", "ypc", "ru_yds", "ru_td"]);
      result = result.filter(c => !rushing.has(c.id));
    }
    if (slateSite !== "both") result = result.filter(c => !c.id.startsWith(slateSite === "dk" ? "fd_" : "dk_"));
    return result;
  }, [activePos, hideWrRushing, baseCols, slateSite]);
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    const statCols = (POS_TO_COLS[activePos] || []).filter(
      (c) =>
        (c.type === "num1" || c.type === "pct") &&
        !c.id.endsWith("_val")
    );
    return rows.filter((r) => {
      const hasStats = statCols.some((c) => num(r[c.id]) !== null);
      const matchesSearch =
        !n ||
        `${r.player} ${r.team || ""} ${r.opp || ""}`
          .toLowerCase()
          .includes(n);
      const matchesTeam =
        selectedTeam === "ALL" ||
        String(r.team || "").toUpperCase() === selectedTeam;
      return hasStats && matchesSearch && matchesTeam;
    });
  }, [rows, q, selectedTeam, activePos]);
  const [sort, setSort] = useState({ key: "dk_proj", dir: "desc" });
  useEffect(() => {
    if (slateSite !== "both") setSort(current => current.key.startsWith(slateSite === "dk" ? "fd_" : "dk_")
      ? { ...current, key: current.key.replace(/^(dk|fd)_/, `${slateSite}_`) } : current);
  }, [slateSite]);
  const sorted = useMemo(() => {
    const { key, dir } = sort;
    const sgn = dir === "asc" ? 1 : -1;
    const out = [...filtered];
    out.sort((a, b) => {
      const isPct = key === "pa_pct" || key === "tgt_share";
      const av = isPct ? num(String(a[key]).replace("%", "")) : num(a[key]);
      const bv = isPct ? num(String(b[key]).replace("%", "")) : num(b[key]);
      const aa = av == null ? -Infinity : av;
      const bb = bv == null ? -Infinity : bv;
      return (aa - bb) * sgn;
    });
    return out;
  }, [filtered, sort]);
  const onSort = (col) =>
    setSort((prev) =>
      prev.key === col.id
        ? { key: col.id, dir: prev.dir === "desc" ? "asc" : "desc" }
        : { key: col.id, dir: "desc" }
    );
  // Auto-hide columns that are empty for this dataset
  const visibleCols = useMemo(() => {
    const always = new Set(["player", "team", "dk_sal", "fd_sal"]);
    return cols.filter((c) => {
      if (always.has(c.id)) return true;
      return sorted.some((r) => {
        const v = r[c.id];
        return v !== "" && v != null;
      });
    });
  }, [cols, sorted]);
  /* --------- heatmap stats (visible rows) -------- */
  const heatStats = useMemo(() => {
    const stats = {};
    if (!sorted.length) return stats;
    for (const c of visibleCols) {
      const d = dirFor(c.id, activePos);
      if (!d) continue;
      let min = Infinity,
        max = -Infinity;
      for (const r of sorted) {
        const v = num(c.type === "pct" ? String(r[c.id]).replace("%", "") : r[c.id]);
        if (v == null) continue;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (min !== Infinity && max !== -Infinity) stats[c.id] = { min, max, dir: d };
    }
    return stats;
  }, [sorted, visibleCols, activePos]);
  // updated timestamp from meta.json
  const updatedStamp = useMemo(() => {
    const art = meta?.artifacts?.find((x) => String(x.path || "").endsWith(src.replace(/^\//, "")));
    const raw = art?.last_updated || art?.tags?.last_updated || meta?.updated_iso || meta?.updated_utc || meta?.last_updated;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }, [meta, src]);
  /* ---------- styling --------- */
  const cell = "px-3 py-1 text-center";
  const headerBase = "px-3 py-1 font-semibold text-center";
  const small = "text-[12px] md:text-[13px]";
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";
  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const headerBgClass = isDark ? "bg-slate-900 text-slate-200" : "bg-slate-50 text-slate-700";
  const headerBgCell = isDark ? "bg-slate-900" : "bg-slate-50";
  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";
  const rowEvenClass = isDark ? "bg-slate-900" : "bg-gray-50";
  const tableTextClass = isDark ? "text-slate-100" : "text-slate-900";
  const posBtnBase = "h-9 px-3 border-b-2 text-xs font-semibold transition";
  const posBtnOn = "border-sky-400 " + (isDark ? "text-white" : "text-slate-900");
  const posBtnOff = isDark
    ? "border-transparent text-slate-400 hover:text-white"
    : "border-transparent text-slate-500 hover:text-slate-900";
  return (
    <div className={outerClass}>
      <div className="w-full px-4 md:px-6 py-5">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">NFL — Showdown Positional Projections</h1>
            <span className={"text-xs italic " + (isDark ? "text-slate-400" : "text-slate-500")}>Updated: {updatedStamp ?? "—"}</span>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
          </div>
                </header>
        <section aria-label="Slate and site" className={cardClass + " mb-4"}>
          <div className="flex flex-wrap items-end gap-3 px-4 py-3">
            <label className="flex min-w-0 flex-col gap-1 text-[11px] font-semibold">
              <span className={isDark ? "text-slate-400" : "text-slate-500"}>Slate</span>
            <select aria-label="Showdown slate" value={slate}
              disabled={!availableSlates.length} onChange={event => setSlateSelection({ site: slateSite, key: event.target.value, selectedAt: Date.now() })}
              className={"h-8 max-w-full min-w-0 rounded-md border px-2 text-xs font-normal " + (isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300")}>
              {!availableSlates.length && <option value="none">No showdown slates available</option>}
              {availableSlates.map(s => <option key={s.key} value={s.key}>{s.label || s.key}</option>)}
            </select>
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[11px] font-semibold">
              <span className={isDark ? "text-slate-400" : "text-slate-500"}>Site</span>
            <select aria-label="Slate site" value={slateSite} onChange={event => setSlateSite(event.target.value)}
              className={"h-8 rounded-md border px-2 text-xs font-normal " + (isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300")}>
              <option value="both">Both sites</option><option value="dk">DraftKings</option><option value="fd">FanDuel</option>
            </select>
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[11px] font-semibold">
              <span className={isDark ? "text-slate-400" : "text-slate-500"}>Roster role</span>
            <select aria-label="Roster role" value={rosterRole} onChange={event => setRosterRole(event.target.value)}
              className={"h-8 rounded-md border px-2 text-xs font-normal " + (isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300")}>
              <option value="flex">FLEX</option><option value="multiplier">{slateSite === "dk" ? "CPT" : slateSite === "fd" ? "MVP" : "CPT / MVP"}</option>
            </select>
            </label>
          </div>
        </section>
        <section aria-label="Positional projections" className={cardClass}>
          <div className={"flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <h2 className="text-sm font-semibold">{activePos} Projections <span className={"ml-2 text-xs font-normal " + (isDark ? "text-slate-400" : "text-slate-500")}>{!loading && !err ? `${sorted.length.toLocaleString()} players` : ""}</span></h2>
            <button
              className="h-8 px-3 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400"
              onClick={() =>
                downloadCSV(
                  sorted,
                  visibleCols,
                  `nfl_showdown_${slate}_${slateSite}_${rosterRole}_${activePos.toLowerCase()}_projections.csv`
                )
              }
            >
              Export CSV
            </button>
          </div>
          <div className="px-4 pt-2">
            <div className={"inline-flex max-w-full border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <div className="flex flex-wrap items-center gap-1">
              {POS_LIST.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setActivePos(p)}
                  className={`${posBtnBase} ${activePos === p ? posBtnOn : posBtnOff}`}
                  title={`Show ${p}`}
                >
                  {p}
                </button>
              ))}
            </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <input
              className={
                "h-8 w-full sm:w-64 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
              }
              placeholder="Search player / team…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className={
                "h-8 md:h-8 rounded-md border px-2 text-xs font-normal focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100"
                  : "bg-white border-slate-300 text-slate-900")
              }
              title="Filter by team"
            >
              <option value="ALL">All Teams</option>
              {teams.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </select>
            {activePos === "WR" && (
              <label
                className={
                  "inline-flex h-8 items-center gap-2 rounded-md border px-3 text-xs cursor-pointer select-none " +
                  (isDark
                    ? "bg-slate-950/80 border-slate-700 text-slate-200"
                    : "bg-white border-slate-300 text-slate-700")
                }
              >
                <input
                  type="checkbox"
                  checked={hideWrRushing}
                  onChange={(e) => setHideWrRushing(e.target.checked)}
                  className="h-4 w-4 accent-sky-500"
                />
                <span>Hide WR rushing stats</span>
              </label>
            )}
          </div>
          <div className="overflow-auto max-h-[70vh] rounded-b-2xl">
            <table className={`w-max min-w-full border-separate ${small} ${tableTextClass}`} style={{ borderSpacing: 0 }}>
              <thead className={`${headerBgClass} sticky top-0 z-20 shadow-[0_2px_0_0_rgba(0,0,0,0.35)]`}>
                <tr>
                  {visibleCols.map((c) => (
                    <th
                      key={c.id}
                      className={`${headerBase} whitespace-nowrap cursor-pointer select-none ${c.w || ""} ${headerBgCell} ${
                        c.id === "player" ? "sticky left-0 z-30" : ""
                      }`}
                      title="Click to sort"
                      onClick={() => onSort(c)}
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>{c.label}</span>
                        <span className="text-gray-400">
                          {sort.key === c.id ? (sort.dir === "desc" ? "▼" : "▲") : "▲"}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td className={`${cell} text-slate-400`} colSpan={visibleCols.length}>
                      Loading…
                    </td>
                  </tr>
                )}
                {err && (
                  <tr>
                    <td className={`${cell} text-red-400`} colSpan={visibleCols.length}>
                      Failed to load: {err}
                    </td>
                  </tr>
                )}
                {!loading &&
                  !err &&
                  sorted.map((r, i) => {
                    const rowBg = i % 2 === 0 ? rowOddClass : rowEvenClass;
                    return (
                      <tr key={`${r.player || i}-${i}`} className={rowBg}>
                        {visibleCols.map((c) => {
                          if (c.id === "player") {
                            const val = r[c.id] ?? "";
                            return (
                              <td
                                key={c.id}
                                className={`px-3 py-1 text-left sticky left-0 z-10 ${rowBg} shadow-[inset_-6px_0_8px_-6px_rgba(0,0,0,0.6)] whitespace-nowrap`}
                                title={String(val)}
                              >
                                <div className="flex items-center gap-2">
                                  {r.team ? (
                                    <img
                                      src={teamLogo(r.team)}
                                      alt=""
                                      className="h-4 w-4 object-contain rounded-sm"
                                      onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                                    />
                                  ) : null}
                                  <span>{val}</span>
                                </div>
                              </td>
                            );
                          }
                          if (c.type === "team") {
                            const abbr = String(r[c.id] || "").toUpperCase();
                            return (
                              <td key={c.id} className={`px-3 py-1 text-center whitespace-nowrap`} title={abbr}>
                                <div className="inline-flex items-center gap-2 justify-center">
                                  {abbr && (
                                    <img
                                      src={teamLogo(abbr)}
                                      alt={abbr}
                                      className="h-4 w-4 object-contain"
                                    />
                                  )}
                                  <span>{abbr}</span>
                                </div>
                              </td>
                            );
                          }
                          const stat = heatStats[c.id];
                          const vNum = num(c.type === "pct" ? String(r[c.id]).replace("%", "") : r[c.id]);
                          const textColor = stat
                            ? heatTextColor(stat.min, stat.max, vNum, stat.dir, theme)
                            : null;
                          let val = r[c.id] ?? "";
                          if (c.type === "money") val = int0(val);
                          else if (c.type === "pct") val = pct(val);
                          else if (c.type === "num1") val = smart1(val);
                          return (
                            <td
                              key={c.id}
                              className={`${cell} tabular-nums whitespace-nowrap`}
                              title={String(val)}
                              style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                            >
                              {val}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
